import React, { createContext, useContext, useEffect, useState } from 'react';
import VeradigmModal from '../../app/my-patients/pages/patient/VeradigmModal';
import {
  EPrescribeSsoMode,
  usePrescribeVeradigmMutation,
} from 'graphql/generated/hasura';
import { useCustomToast } from 'layout/useToast';
import { useApolloClient } from '@apollo/client';

interface VeradigmContextType {
  isOpen: boolean;
  isLoading: boolean;
  openModal: (mode: EPrescribeSsoMode) => void;
  closeModal: () => void;
}

const VeradigmContext = createContext<VeradigmContextType | undefined>(
  undefined,
);

export const VeradigmProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<EPrescribeSsoMode>(
    EPrescribeSsoMode.SsoidProofing,
  );
  const { addToast } = useCustomToast();
  const client = useApolloClient();

  const [getToken, { data: veradigmToken, loading: veradigmLoading }] =
    usePrescribeVeradigmMutation({
      onError: (error) => {
        setIsOpen(false);
        addToast({ type: 'warning', message: error.message });
      },
    });

  useEffect(() => {
    if (isOpen) {
      getToken({ variables: { mode } });
    }
  }, [isOpen, getToken, mode]);

  const openModal = (mode: EPrescribeSsoMode) => {
    setMode(mode);
    setIsOpen(true);
  };
  const closeModal = () => {
    client.refetchQueries({
      include: ['GetFhirProviderbyEmail'],
    });
    setMode(EPrescribeSsoMode.SsoidProofing);
    setIsOpen(false);
  };

  return (
    <VeradigmContext.Provider
      value={{
        isOpen,
        isLoading: veradigmLoading,
        openModal,
        closeModal,
      }}
    >
      {children}

      {veradigmToken?.ePrescribeJwt?.jwt && (
        <VeradigmModal
          token={veradigmToken.ePrescribeJwt.jwt}
          isOpen={isOpen}
          setIsOpen={closeModal}
        />
      )}
    </VeradigmContext.Provider>
  );
};

export const useVeradigm = () => {
  const context = useContext(VeradigmContext);

  if (context === undefined) {
    throw new Error('useVeradigm must be used within a Veradigm Provider');
  }

  return context;
};
